import { strToU8, compressSync, decompressSync, strFromU8 } from 'fflate';

const PROTOCOL_VERSION = 'v1';
const MAGIC_HEADER = 'SEEDE';

/**
 * Common keys mapping for shorter JSON and basic obfuscation.
 *
 * This is the canonical short-key dictionary used by the `.seede`
 * envelope. Anything that decodes a `.seede` file (including test
 * helpers in other packages) MUST derive its reverse map from this
 * one to avoid drift.
 */
export const KEY_MAP = {
  id: 'i',
  type: 't',
  name: 'n',
  index: 'x',
  parentId: 'p',
  x: 'a',
  y: 'b',
  w: 'w',
  h: 'h',
  fill: 'f',
  stroke: 's',
  strokeWidth: 'sw',
  text: 'txt',
  styles: 'st',
  highlights: 'hi',
  meta: 'm',
  rotation: 'r',
  opacity: 'o',
  radius: 'rd',
  visible: 'v',
  locked: 'l',
  padding: 'pd',
  transform: 'tr',
  src: 'src',
  fontSize: 'fs',
  fontFamily: 'ff',
  fontWeight: 'fw',
  lineHeight: 'lh',
  letterSpacing: 'ls',
  textAlign: 'ta',
  verticalAlign: 'va',
  color: 'c',
  messages: 'msg',
  config: 'cfg',
  chatSessions: 'cs',
  logs: 'lg',
  versions: 'vs',
};

export const REVERSE_KEY_MAP = Object.fromEntries(Object.entries(KEY_MAP).map(([k, v]) => [v, k]));

/**
 * Deeply transform keys of an object based on the map
 */
function transformKeys(obj: any, map: Record<string, string>): any {
  if (Array.isArray(obj)) {
    return obj.map((item) => transformKeys(item, map));
  }
  if (obj !== null && typeof obj === 'object') {
    const result: any = {};
    for (const key in obj) {
      if (key.startsWith('_')) continue; // Skip private internal keys
      const newKey = map[key] || key;
      result[newKey] = transformKeys(obj[key], map);
    }
    return result;
  }
  return obj;
}

/**
 * Convert Uint8Array to Base64 string efficiently
 */
function uint8ToBase64(uint8: Uint8Array): string {
  let binary = '';
  const len = uint8.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(uint8[i]!);
  }
  return btoa(binary);
}

/**
 * Convert Base64 string to Uint8Array efficiently
 */
function base64ToUint8(base64: string): Uint8Array {
  const binary = atob(base64);
  const uint8 = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    uint8[i] = binary.charCodeAt(i);
  }
  return uint8;
}

interface SerializedData {
  v: string; // protocol version
  n: any; // nodes (transformed)
  m?: any[]; // messages (transformed)
  c?: any; // config (transformed)
  i?: any; // inputs (original)
  cs?: any; // chatSessions (transformed)
  lg?: any; // logs (transformed, veeso dev)
  vs?: any; // versions (transformed)
}

export interface SeedeGraphic {
  type: 'seedeGraphic';
  messages: any[];
  config?: Record<string, any>;
  chatSessions?: any;
  logs?: Record<string, unknown>;
  versions?: Record<string, any>;
  props: {
    inputs?: Record<string, unknown>;
    nodes: Record<string, any>;
  };
}

/**
 * Private serialization: State Object -> Compressed String
 * Implementation for StateAPI.export
 */
export function serialize(state: SeedeGraphic): string {
  const transformed: SerializedData = {
    v: PROTOCOL_VERSION,
    n: transformKeys(state.props.nodes, KEY_MAP),
  };

  if (state.messages) {
    transformed.m = transformKeys(state.messages, KEY_MAP);
  }

  if (state.config) {
    transformed.c = transformKeys(state.config, KEY_MAP);
  }

  if (state.props.inputs) {
    transformed.i = state.props.inputs;
  }

  if (state.chatSessions) {
    transformed.cs = transformKeys(state.chatSessions, KEY_MAP);
  }

  if (state.logs) {
    transformed.lg = transformKeys(state.logs, KEY_MAP);
  }

  if (state.versions) {
    transformed.vs = transformKeys(state.versions, KEY_MAP);
  }

  const json = JSON.stringify(transformed);
  const uint8 = strToU8(json);
  const compressed = compressSync(uint8);
  const base64 = uint8ToBase64(compressed);

  return `${MAGIC_HEADER}:${base64}`;
}

/**
 * Private deserialization: Compressed String -> State Object
 * Implementation for StateAPI.import
 */
export function deserialize(data: string): SeedeGraphic {
  // Remove leading and trailing double quotes if present
  if (data.startsWith('"') && data.endsWith('"')) {
    data = data.slice(1, -1);
  }
  if (typeof data !== 'string' || !data.startsWith(`${MAGIC_HEADER}:`)) {
    // If it's a plain JSON string, try to parse it (compatibility)
    try {
      const parsed = JSON.parse(data);
      if (parsed && (parsed.nodes || (parsed.props && parsed.props.nodes))) {
        if (parsed.type === 'seedeGraphic') return parsed;

        // Convert old format to new format
        return {
          type: 'seedeGraphic',
          messages: parsed.messages || [],
          config: parsed.config || {},
          chatSessions: parsed.chatSessions,
          logs: parsed.logs,
          versions: parsed.versions || {},
          props: {
            nodes: parsed.nodes || parsed.props?.nodes || {},
          },
        };
      }
    } catch {
      // Not a valid JSON or our protocol
    }
    throw new Error('Invalid serialization format');
  }

  const base64 = data.slice(MAGIC_HEADER.length + 1);
  const uint8 = base64ToUint8(base64);
  const decompressed = decompressSync(uint8);
  const json = strFromU8(decompressed);
  const transformed = JSON.parse(json) as SerializedData;

  if (transformed.v !== PROTOCOL_VERSION) {
    throw new Error(`Unsupported protocol version: ${transformed.v}`);
  }

  return {
    type: 'seedeGraphic',
    messages: transformed.m ? transformKeys(transformed.m, REVERSE_KEY_MAP) : [],
    config: transformed.c ? transformKeys(transformed.c, REVERSE_KEY_MAP) : {},
    chatSessions: transformed.cs ? transformKeys(transformed.cs, REVERSE_KEY_MAP) : undefined,
    logs: transformed.lg ? transformKeys(transformed.lg, REVERSE_KEY_MAP) : undefined,
    versions: transformed.vs ? transformKeys(transformed.vs, REVERSE_KEY_MAP) : {},
    props: {
      inputs: transformed.i,
      nodes: transformKeys(transformed.n, REVERSE_KEY_MAP),
    },
  };
}
